import { loaders, BaseTexture, Texture } from 'pixi.js';
import IResourceDriver, { IQueueItem } from './IResourceDriver';
import ResourceManager from './ResourceManager';
import HowlerMiddleware from './HowlerPixiLoaderMiddleware';
import ResourceDriverManager from './ResourceDriverManager';

export default class PIXIResource implements IResourceDriver {
    private manager: ResourceDriverManager;
    private resourceManager: ResourceManager;
    private concurrency: number;

    private loader: loaders.Loader;
    private queue: IQueueItem[];

    public init(
        manager: ResourceDriverManager,
        resourceManager: ResourceManager,
        concurrency?: number
    ) {
        this.manager = manager;
        this.resourceManager = resourceManager;

        // The number of resources to load concurrently.
        this.concurrency = !concurrency || concurrency <= 0 ? 0 : concurrency;

        this.loader = new loaders.Loader();
        this.loader.use(HowlerMiddleware);
        this.queue = [];
    }

    /**
     * Queue a resource for loading.
     * @param resourceID
     * @param path
     */
    public add(resourceID: string, path: string): void {
        this.queue.push({ resourceID: resourceID, path: path });
    }

    /**
     * Start to loading.
     */
    public async load() {
        return new Promise((resolve, reject) => {
            this.loader.once('complete', () => {
                this.loadDone();
                resolve();

                this.loader.resources = {};
            });

            this.loader.on('error', (_error, _loader, resource) => {
                // Clear texture caches.
                BaseTexture.removeFromCache(resource.name);
                BaseTexture.removeFromCache(resource.url);
                Texture.removeFromCache(resource.name);
                Texture.removeFromCache(resource.url);

                // Inform that this resource fail to load.
                this.manager.failToLoad(resource.name);
            });

            this.loader.on('progress', (loader: loaders.Loader) => {
                this.manager.setProgress(loader.progress);
            });

            this.loader.on('load', this.onLoaded, this);

            const len = this.queue.length;
            const concurrency =
                this.concurrency === 0 || this.concurrency > len
                    ? len
                    : this.concurrency;
            this.queue.splice(0, concurrency).forEach(item => {
                try {
                    this.loader.add(item.resourceID, item.path, {
                        crossOrigin: 'anonymous',
                    });
                } catch (e) {
                    this.manager.failToLoad(item.resourceID);
                }
            });

            // Start loading queued resources.
            this.loader.load();
        });
    }

    public stop(): void {
        this.queue = [];
    }

    public getQueue(): IQueueItem[] {
        return this.queue;
    }

    public isLoading(): boolean {
        return this.loader.loading;
    }

    private onLoaded(_: loaders.Loader, resource: loaders.Resource): void {
        if (resource.texture) {
            // Set 'texture' as cached-resource.
            this.resourceManager.set(resource.name, resource.texture);
        } else {
            // Set raw data as cached-resource.
            this.resourceManager.set(resource.name, resource.data);
        }

        // Set any and all texture-atlas textures as their own resource when the texture-atlas is loaded.
        if (resource.textures) {
            Object.entries(resource.textures).forEach(([key, value]) =>
                this.resourceManager.set(key, value)
            );
        }
    }

    /**
     * Remove listeners.
     */
    private loadDone(): void {
        this.loader.off('complete');
        this.loader.off('error');
        this.loader.off('progress');
        this.loader.off('load');
    }
}
